import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  Button
} from '@mui/material';
import {
  School as SchoolIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import Loading from '../common/Loading';

const PedagogicalSituation = () => {
  const [situationData, setSituationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  // --- HELPER: MERGE & PROCESS DATA ---
const processSituationData = (data) => {
  if (!data) return null;
  
  // Initialize merged structure
  const merged = {
    yearly_elements: {},
    semester_elements: {}
  };

  // Helper to merge arrays without duplicates
  const mergeModules = (target, source) => {
    if (!source) return;
    Object.entries(source).forEach(([key, modules]) => {
      if (!target[key]) target[key] = [];
      const existingCodes = new Set(target[key].map(m => m.cod_elp));
      
      modules.forEach(m => {
        const code = m.cod_elp || '';
        // FILTER: Hide specific technical codes
        if (code.endsWith('005') || code.endsWith('CC') || code.endsWith('15') || code.endsWith('25')) return;
        
        if (!existingCodes.has(m.cod_elp)) {
          target[key].push(m);
        }
      });
    });
  };

  // 1. Initial Merge: Check if data is already organized or wrapped in years
  if (data.yearly_elements || data.semester_elements) {
    mergeModules(merged.yearly_elements, data.yearly_elements);
    mergeModules(merged.semester_elements, data.semester_elements);
  } else {
    Object.values(data).forEach(yearData => {
      mergeModules(merged.yearly_elements, yearData.yearly_elements);
      mergeModules(merged.semester_elements, yearData.semester_elements);
    });
  }

  // 2. RE-CLASSIFICATION LOGIC: Ensure "AN%" COD_NEL go to Yearly
  // We check the semester_elements (especially 'Unknown') for yearly indicators
  Object.entries(merged.semester_elements).forEach(([semKey, modules]) => {
    const remainingModules = [];
    
    modules.forEach(mod => {
      const modName = (mod.lib_elp || '').toLowerCase();
      const modCode = (mod.cod_elp || '').toUpperCase();
      const modNel = (mod.cod_nel || '').toUpperCase();

      // NEW CHECK: If COD_NEL starts with AN or name implies year
      const isYearly = modNel.startsWith('AN') || 
                       mod.element_type === 'ANNEE' || 
                       modName.includes('année') || 
                       modName.includes('year');

      if (isYearly) {
        let level = mod.academic_level || 'Unknown';
        
        // Refine level if it's currently Unknown
        if (level === 'Unknown') {
          if (modName.includes('première') || modCode.includes('0A1') || modNel === 'AN1') level = '1A';
          else if (modName.includes('deuxième') || modCode.includes('0A2') || modNel === 'AN2') level = '2A';
          else if (modName.includes('troisième') || modCode.includes('0A3') || modNel === 'AN3') level = '3A';
        }

        if (!merged.yearly_elements[level]) merged.yearly_elements[level] = [];
        
        // Prevent duplicates in Yearly section
        if (!merged.yearly_elements[level].some(m => m.cod_elp === mod.cod_elp)) {
          merged.yearly_elements[level].push(mod);
        }
      } else {
        remainingModules.push(mod);
      }
    });

    // Update the semester elements list
    if (remainingModules.length > 0) {
      merged.semester_elements[semKey] = remainingModules;
    } else {
      delete merged.semester_elements[semKey];
    }
  });

  // Final cleanup: Remove empty keys
  Object.keys(merged.yearly_elements).forEach(key => {
    if (merged.yearly_elements[key].length === 0) delete merged.yearly_elements[key];
  });

  return merged;
};

  const fetchPedagogicalSituation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Fetch without year filter to get everything (which is just the current sync)
      const situationResponse = await studentAPI.getPedagogicalSituation();

      const cleanData = processSituationData(situationResponse.pedagogical_situation);
      setSituationData(cleanData);
      
    } catch (err) {
      setError(err.response?.data?.error || 'فشل في تحميل الوضعية البيداغوجية');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchPedagogicalSituation(); }, []);

  // --- HELPERS (Colors) ---
  const getSemesterColor = (semester) => {
    if (semester === 'Unknown' || semester === 'Annuel') return '#8e44ad';
    const colors = { 'S1': '#3498db', 'S2': '#e67e22', 'S3': '#2ecc71', 'S4': '#9b59b6', 'S5': '#e74c3c', 'S6': '#34495e' };
    return colors[semester] || '#95a5a6';
  };
  const getAcademicLevelColor = (level) => {
    const colors = { '1A': '#3498db', '2A': '#e67e22', '3A': '#2ecc71', '4A': '#9b59b6', '5A': '#e74c3c' };
    return colors[level] || '#95a5a6';
  };
  const getAcademicLevelLabel = (level) => {
    const key = `ps_level${level}`;
    return t(key, { defaultValue: level });
  };

  // --- MODULE ROW (shared between yearly and semester) ---
  const renderModuleRow = (module, index, accentColor) => (
    <Box
      key={`${module.cod_elp}-${index}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: 2, py: 1.5,
        borderRadius: 2,
        bgcolor: 'white',
        border: '1px solid #f0f0f0',
        '&:hover': { bgcolor: '#fafafa', borderColor: accentColor + '55' },
        transition: 'all 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
          bgcolor: accentColor + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <AssignmentIcon sx={{ fontSize: 18, color: accentColor }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
            {module.lib_elp_arb || module.lib_elp || '—'}
          </Typography>
          {module.lib_elp_arb && module.lib_elp && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
              {module.lib_elp}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1, color: '#475569', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
          {module.cod_elp}
        </Typography>
        {module.group_name && (
          <Chip
            label={`${t('ps_group')} ${module.group_name}`}
            size="small"
            sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontWeight: 700, height: 22, borderRadius: 1.5 }}
          />
        )}
      </Box>
    </Box>
  );

  // --- SECTION BLOCK (shared header + module list) ---
  const renderSection = (label, subLabel, modules, color, icon) => (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, border: `1.5px solid ${color}33`, overflow: 'hidden', mb: 2 }}
    >
      <Box sx={{
        px: 2.5, py: 1.8,
        bgcolor: color + '12',
        borderBottom: `1px solid ${color}33`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
            {label}
          </Typography>
          {subLabel && (
            <Typography variant="caption" color="text.secondary">{subLabel}</Typography>
          )}
        </Box>
        <Chip
          label={t('ps_nModules', { count: modules.length })}
          size="small"
          sx={{ bgcolor: color + '22', color, fontWeight: 700, height: 22, borderRadius: 1.5 }}
        />
      </Box>
      <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {modules
          .sort((a, b) => (a.cod_elp || '').localeCompare(b.cod_elp || ''))
          .map((mod, i) => renderModuleRow(mod, i, color))}
      </Box>
    </Paper>
  );

  const renderYearlyElements = (yearlyElements) => {
    if (!yearlyElements || Object.keys(yearlyElements).length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <CalendarIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body1">{t('ps_noYearly')}</Typography>
        </Box>
      );
    }
    return (
      <Box>
        {Object.entries(yearlyElements)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([level, modules]) =>
            renderSection(
              `${level} — ${getAcademicLevelLabel(level)}`,
              null,
              modules,
              getAcademicLevelColor(level),
              <CalendarIcon sx={{ fontSize: 17, color: getAcademicLevelColor(level) }} />
            )
          )}
      </Box>
    );
  };

  const renderSemesterElements = (semesterElements) => {
    if (!semesterElements || Object.keys(semesterElements).length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body1">{t('ps_noSemester')}</Typography>
        </Box>
      );
    }
    return (
      <Box>
        {Object.entries(semesterElements)
          .sort(([a], [b]) => {
            if (a === 'Unknown') return 1;
            if (b === 'Unknown') return -1;
            return (parseInt(a.replace('S', '')) || 0) - (parseInt(b.replace('S', '')) || 0);
          })
          .map(([semester, modules]) => {
            let displaySemester = semester;
            let label, subLabel;

            if (semester === 'Unknown') {
              const hasYearly = modules.some(m => m.element_type === 'ANNEE' || (m.lib_elp || '').toLowerCase().includes('année'));
              if (hasYearly) {
                displaySemester = 'Annuel';
                label = t('ps_annual');
              } else {
                label = t('ps_unassigned');
              }
              subLabel = null;
            } else {
              label = `${t('ps_semester')} ${semester.replace('S', '')}`;
              subLabel = semester;
            }

            const color = getSemesterColor(displaySemester === 'Annuel' ? 'Annuel' : semester);
            return renderSection(
              label, subLabel, modules, color,
              <TimelineIcon sx={{ fontSize: 17, color }} />
            );
          })}
      </Box>
    );
  };

  if (isLoading) return <Loading message={t('ps_loading')} />;

  const totalCount = Object.values(situationData?.semester_elements || {}).flat().length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{t('ps_title')}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>{t('ps_subtitle')}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {totalCount > 0 && (
            <Chip
              label={t('ps_nModules', { count: totalCount })}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700 }}
            />
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchPedagogicalSituation}
            size="small"
            sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 2, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            {t('ps_refresh')}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {situationData ? (
        <Box>
          {renderSemesterElements(situationData.semester_elements)}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <SchoolIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>{t('ps_noData')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('ps_noDataDesc')}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PedagogicalSituation;